import { PROFILE_TAG_REGEX, SourceTypes } from '../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../utils';
import type { DataValue, PictureValueType, ProviderType } from '../types';
import {
  pinterestBoardFullURLRegExp,
  pinterestBoardShortURLRegExp,
  pinterestProfileFullURLRegExp
} from './pinterestURLPattern';
import type { PinterestResponseType, PinterestSourceValue } from './types';

const PLATFORM = SourceTypes.PINTEREST;

const select = (
  response: PinterestResponseType
): DataValue<PictureValueType[]>[] => {
  handleResponseError(response, PLATFORM, 'data.pins');

  return response.data.pins.map(({ description, id, images, pinner }) => ({
    id,
    text: formatText(description, PLATFORM),
    timestamp: null,
    source: {
      type: PLATFORM,
      url: `https://www.pinterest.com/pin/${id}/`
    },
    user: {
      name: formatText(pinner.full_name, PLATFORM),
      image: pinner.image_small_url,
      url: pinner.profile_url
    },
    media: images
      ? {
          type: 'image',
          src: Object.values(images)
        }
      : undefined,
    counters: {
      comments: 0
    }
  }));
};

export function createPinterestDataProvider(
  sourceValue: PinterestSourceValue,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<PinterestResponseType> {
  const { source = '', connectionType } = sourceValue;

  const key = [...globalKey, ...filtersKey, source];

  const page = (data: PinterestResponseType | undefined) => {
    if (data) {
      return;
    }

    const requestURL = (() => {
      if (connectionType === 'pinterestBoard') {
        const [_all, profile, board] =
          source.match(pinterestBoardFullURLRegExp) ??
          source.match(pinterestBoardShortURLRegExp) ??
          [];

        return `boards/${profile}/${board}/pins`;
      }

      if (connectionType === 'pinterestProfile') {
        const [_all, profile] =
          source.match(PROFILE_TAG_REGEX) ??
          source.match(pinterestProfileFullURLRegExp) ??
          [];

        return `users/${profile}/pins`;
      }

      return '';
    })();

    return buildProxyURL({ apiSource: PLATFORM, params: { q: requestURL } });
  };

  return { key, page, select };
}
